import React, { useEffect, useState } from "react";
import { BarChart3, LogOut, ShieldCheck } from "lucide-react";
import { api, API } from "../api";
import { ClientAuth, errText } from "../components/ClientAuth";
import { AnalyticsStrategyReport } from "../components/AnalyticsStrategyReport";
import { PairingPanel } from "../components/ClientPairing";

const TOKEN_KEY = "dtsp_client_token";
const money = (v) => `${v > 0 ? "+" : ""}${Number(v || 0).toFixed(2)}$`;

const ReportView = ({ r, onPdf, pdfBusy }) => (
  <div data-testid="client-report">
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
      {[["Trades", r.total_trades, "client-kpi-trades"], ["Taux de réussite", `${r.winrate ?? 0}%`, "client-kpi-winrate"],
        ["P&L net", money(r.net_pnl), "client-kpi-pnl"], ["Pertes profondes", r.deep_losses ?? 0, "client-kpi-deep"]].map(([l, v, id]) => (
        <div key={id} className="glass rounded-xl p-4">
          <div className="text-[11px] uppercase tracking-wider text-zinc-500">{l}</div>
          <div className="text-xl font-bold font-mono mt-1" data-testid={id}>{v}</div>
        </div>
      ))}
    </div>
    <div className="glass rounded-xl p-4 mb-6 text-sm text-zinc-300 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
      <span data-testid="client-report-summary">{r.summary}</span>
      <button onClick={onPdf} disabled={pdfBusy} data-testid="client-export-pdf-btn"
              className="btn-ghost px-4 py-2 rounded-lg text-xs whitespace-nowrap disabled:opacity-50">
        {pdfBusy ? "Préparation…" : "📄 Exporter le PDF"}
      </button>
    </div>
    <AnalyticsStrategyReport strategies={r.by_strategy} />
  </div>
);

const SchedulePanel = ({ headers, account }) => {
  const [s, setS] = useState(null);
  const [msg, setMsg] = useState("");
  useEffect(() => {
    api.get("/client/analytics/email-schedule", headers).then(({ data }) => setS({ frequency: "weekly", day_of_week: 0, day_of_month: 1, hour_utc: 7, window_days: 30, ...data })).catch(() => {});
  }, []); // eslint-disable-line
  if (!s) return null;
  const save = async (enabled) => {
    setMsg("");
    try {
      const { data } = await api.post("/client/analytics/email-schedule", { ...s, enabled, account }, headers);
      setS({ ...s, ...data.schedule }); setMsg(enabled ? "✓ Envoi programmé enregistré." : "Envoi programmé désactivé.");
    } catch (e) { setMsg(errText(e)); }
  };
  const sel = "px-3 py-2 rounded-lg text-sm bg-black/40 border border-white/10 w-full";
  return (
    <div className="glass rounded-xl p-5 mt-6" data-testid="client-schedule">
      <h2 className="text-sm font-bold mb-1">📧 Rapport programmé par email</h2>
      <p className="text-xs text-zinc-400 mb-3">Le PDF du compte <span className="font-mono">{account}</span> est envoyé à {s.email}.</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <select className={sel} value={s.frequency} onChange={(e) => setS({ ...s, frequency: e.target.value })} data-testid="client-schedule-frequency">
          <option value="daily">Quotidien</option><option value="weekly">Hebdomadaire</option><option value="monthly">Mensuel</option>
        </select>
        {s.frequency === "weekly" && (
          <select className={sel} value={s.day_of_week} onChange={(e) => setS({ ...s, day_of_week: +e.target.value })} data-testid="client-schedule-dow">
            {["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"].map((d, i) => <option key={d} value={i}>{d}</option>)}
          </select>
        )}
        {s.frequency === "monthly" && (
          <select className={sel} value={s.day_of_month} onChange={(e) => setS({ ...s, day_of_month: +e.target.value })} data-testid="client-schedule-dom">
            {Array.from({ length: 28 }, (_, i) => <option key={i} value={i + 1}>Le {i + 1}</option>)}
          </select>
        )}
        <select className={sel} value={s.hour_utc} onChange={(e) => setS({ ...s, hour_utc: +e.target.value })} data-testid="client-schedule-hour">
          {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, "0")}h00 UTC</option>)}
        </select>
        <select className={sel} value={s.window_days} onChange={(e) => setS({ ...s, window_days: +e.target.value })} data-testid="client-schedule-window">
          {[7, 30, 90].map((d) => <option key={d} value={d}>{d} derniers jours</option>)}
        </select>
      </div>
      <div className="flex flex-wrap items-center gap-3 mt-4">
        <button onClick={() => save(true)} className="btn-primary px-4 py-2 rounded-lg text-xs font-semibold" data-testid="client-schedule-save">
          {s.enabled ? "Mettre à jour" : "Activer l'envoi"}
        </button>
        {s.enabled && <button onClick={() => save(false)} className="btn-ghost px-4 py-2 rounded-lg text-xs" data-testid="client-schedule-disable">Désactiver</button>}
        {msg && <span className="text-xs text-zinc-300" data-testid="client-schedule-msg">{msg}</span>}
      </div>
    </div>
  );
};

const Workspace = ({ token, email, onLogout }) => {
  const headers = { headers: { "x-client-token": token } };
  const [linked, setLinked] = useState(null);
  const [days, setDays] = useState(30);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [err, setErr] = useState("");

  const generate = async () => {
    setBusy(true); setErr("");
    try { setReport((await api.post(`/client/analytics/generate-report?account=${linked.account}&since_days=${days}`, {}, headers)).data); }
    catch (e) { setErr(errText(e)); }
    finally { setBusy(false); }
  };
  const pdf = async () => {
    setPdfBusy(true);
    try {
      const res = await fetch(`${API}/client/analytics/reports/${report.id}/pdf`, { headers: { "x-client-token": token } });
      if (!res.ok) throw new Error();
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement("a"); a.href = url; a.download = `analytics_${linked.account}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { setErr("Export PDF impossible, réessayez."); }
    finally { setPdfBusy(false); }
  };

  return (
    <>
      <div className="flex items-center justify-between mb-6 text-sm">
        <span className="text-zinc-400">Connecté : <span className="text-white" data-testid="client-email">{email}</span></span>
        <button onClick={onLogout} className="btn-ghost px-3 py-1.5 rounded-lg text-xs inline-flex items-center gap-1.5" data-testid="client-logout-btn">
          <LogOut className="h-3.5 w-3.5" /> Déconnexion
        </button>
      </div>
      <PairingPanel headers={headers} onLinked={(l) => { setLinked(l); setReport(null); }} />
      {linked && (
        <div className="glass rounded-xl p-5 mb-6 flex flex-col sm:flex-row sm:items-end gap-3" data-testid="client-generate-panel">
          <div className="flex-1 text-xs text-zinc-400">
            <div className="text-emerald-300 font-semibold text-sm mb-1" data-testid="client-account-linked">
              ✓ Compte {linked.account} appairé
            </div>
            {linked.trades} trades reçus{linked.broker ? ` · ${linked.broker}` : ""}{linked.last_trade ? ` · dernier : ${linked.last_trade.slice(0, 16).replace("T", " ")} UTC` : ""}
          </div>
          <select value={days} onChange={(e) => setDays(+e.target.value)} data-testid="client-period-select"
                  className="px-3 py-2.5 rounded-lg text-sm bg-black/40 border border-white/10">
            {[7, 30, 90, 180, 365].map((d) => <option key={d} value={d}>{d} derniers jours</option>)}
          </select>
          <button onClick={generate} disabled={busy} data-testid="client-generate-btn"
                  className="btn-primary px-5 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-50">
            {busy ? "Analyse…" : "Analyser mes trades"}
          </button>
        </div>
      )}
      {err && <div className="text-sm text-red-400 mb-4" data-testid="client-report-error">{err}</div>}
      {report && <ReportView r={report} onPdf={pdf} pdfBusy={pdfBusy} />}
      {linked && <SchedulePanel headers={headers} account={linked.account} />}
    </>
  );
};

export default function ClientAnalytics() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [email, setEmail] = useState("");

  useEffect(() => { window.scrollTo(0, 0); }, []);
  useEffect(() => {
    if (!token) return;
    api.get("/client/me", { headers: { "x-client-token": token } })
      .then(({ data }) => setEmail(data.email))
      .catch(() => { localStorage.removeItem(TOKEN_KEY); setToken(null); });
  }, [token]);

  const logged = (t, e) => { localStorage.setItem(TOKEN_KEY, t); setEmail(e); setToken(t); };
  const logout = async () => {
    try { await api.post("/client/logout", {}, { headers: { "x-client-token": token } }); } catch { /* session déjà expirée */ }
    localStorage.removeItem(TOKEN_KEY); setToken(null); setEmail("");
  };

  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16" data-testid="client-analytics-page">
      <div className="mb-10">
        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-cyan-300 mb-3">
          <BarChart3 className="h-4 w-4" /> Espace client
        </div>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">Analysez vos trades</h1>
        <p className="text-zinc-400 mt-3 max-w-2xl text-sm sm:text-base">
          Résultats par stratégie, conditions de vos trades perdants et réglages d'inputs conseillés, calculés sur les trades envoyés par votre EA.
        </p>
        {!token && (
          <div className="flex items-center gap-2 text-xs text-zinc-500 mt-3"><ShieldCheck className="h-3.5 w-3.5" /> Réservé aux clients ayant acheté l'EA.</div>
        )}
      </div>
      {token ? <Workspace token={token} email={email} onLogout={logout} /> : <ClientAuth onLogged={logged} />}
    </section>
  );
}
